import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ReferralPagesColumns as columns } from '../columnsConfig';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import {
  useLazyGetBacklinksCountQuery,
  useGetBacklinksMutation
} from '../api/backlinkBacklinksApi';
import { useExportBacklinkTableMutation } from '../api/exportApi';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import FiltersComponent from '@/app/common/components/filters/FiltersComponent';
import FiltersComponentSkeleton from '@/app/common/components/filters/FiltersComponentSkeleton';
import ApiLoadError from '@/components/errors/ApiLoadError';
import { useLocation, useSearchParams } from 'react-router-dom';
import useBacklinkQueryBody from '../hooks/useBacklinkQueryBody';
import { Card, Nav, Overlay } from 'react-bootstrap';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import numberHumanizer from '@/helpers/numberHumanizer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import {
  BEST_LINK_TOOLTIP,
  HOME_PAGE_TOOLTIP,
  PAGE_UNAVAILABLE_TOOLTIP,
  NOINDEX_TOOLTIP,
  CANONICAL_DIFFERENT_TOOLTIP,
  NEW_LINK_TOOLTIP,
  MOZ_SPAM_SCORE_TOOLTIP,
  BACKLINK_SPAM_SCORE_TOOLTIP
} from '../utils/backlinkCellFlags';

/** Approximate width reserved for the "more tabs" control inside the card header. */
const MORE_TABS_BTN_WIDTH = 96;

const navItemsMap = {
  all: {
    label: 'همه',
    tooltip: 'تمام بک‌لینک‌های ثبت‌شده برای دامنه شما.'
  },
  lost: {
    label: 'حذف لینک',
    tooltip:
      'نشانی مقصد پیوند (href) در صفحه ارجاع‌دهنده (محل قرارگیری لینک) پیدا نشد؛ یا این صفحه وضعیت ۴۰۴ یا ۴۱۰ داشته است.'
  },
  unavailable: {
    label: 'عدم دسترسی',
    tooltip: PAGE_UNAVAILABLE_TOOLTIP
  },
  active: {
    label: 'فعال',
    tooltip:
      'بک‌لینک‌هایی که براساس آخرین خزش، هنوز در صفحهٔ ارجاع‌دهنده وجود دارند و فعال محسوب می‌شوند. لینک‌های حذف‌شده (Lost) و لینک‌هایی که صفحهٔ ارجاع‌دهندهٔ آن‌ها در دسترس نبوده است، در این بخش نمایش داده نمی‌شوند.'
  },
  broken: {
    label: 'شکسته',
    tooltip:
      'مقصد لینک (href) هنگام بررسی توسط میزفا تولز با خطای ۴xx یا ۵xx مواجه بوده است.'
  },
  high_spam: {
    label: 'دامنه اسپم',
    tooltip: MOZ_SPAM_SCORE_TOOLTIP
  },
  backlink_spam: {
    label: 'لینک اسپم',
    tooltip: BACKLINK_SPAM_SCORE_TOOLTIP
  },
  noindex: {
    label: 'نولیندکس',
    tooltip: NOINDEX_TOOLTIP
  },
  canonical: {
    label: 'کنونیکال',
    tooltip: CANONICAL_DIFFERENT_TOOLTIP
  },
  nofollow: {
    label: 'نوفالو',
    tooltip: 'پیوندهایی با rel=nofollow یا dofollow=false.'
  },
  redirected: {
    label: 'ریدایرکت‌ها',
    tooltip: 'پیوندهایی که مقصد یا صفحه منبع آن‌ها ریدایرکت دارد.'
  },
  best: {
    label: 'بهترین',
    tooltip: BEST_LINK_TOOLTIP
  },
  in_text: {
    label: 'در متن',
    tooltip:
      'در اطراف لینک شما متن کافی وجود دارد و این موضوع ارزش لینک را بالاتر می‌برد.'
  },
  new: {
    label: 'جدید',
    tooltip: NEW_LINK_TOOLTIP
  },
  home: {
    label: 'از خانه',
    tooltip: HOME_PAGE_TOOLTIP
  },
  follow: {
    label: 'فالو',
    tooltip: 'پیوندهای dofollow.'
  }
};

const navItems = Object.entries(navItemsMap);
const backlinkTabKeys = new Set(['all', ...Object.keys(navItemsMap)]);

const normalizeBacklinkTab = (tab: string | null): string =>
  tab && backlinkTabKeys.has(tab) ? tab : 'all';

const Backlinks = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const body = useBacklinkQueryBody();
  const searchQuery = searchParams.toString();
  const { domain_slug, grouping } = body;
  const requestedTab = normalizeBacklinkTab(searchParams.get('tab'));
  const [activeTab, setActiveTab] = useState(requestedTab);
  const headerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const moreBtnRef = useRef<HTMLButtonElement>(null);
  const tabWidthsRef = useRef<Record<string, number>>({});
  const [visibleTabCount, setVisibleTabCount] = useState(navItems.length);
  const [moreOpen, setMoreOpen] = useState(false);

  const skipQueries = !domain_slug;

  const [
    getBacklinksCount,
    {
      data: gridData,
      isFetching: isGridFetching,
      isLoading: isGridLoading,
      isError: isGridError,
      error: gridError
    }
  ] = useLazyGetBacklinksCountQuery();

  const [
    getBacklinksFunc,
    {
      data: mutationData,
      isSuccess,
      isError: isGridMutationError,
      error: gridMutationError,
      isLoading: isGridMutationLoading,
      reset: resetGridMutation
    }
  ] = useGetBacklinksMutation();
  const [exportTable] = useExportBacklinkTableMutation();

  const retryGrid = () =>
    getBacklinksCount({
      domain_slug,
      grouping,
      tab: activeTab,
      page: 1,
      perPage: 50
    });

  useEffect(() => {
    if (skipQueries) return;
    getBacklinksCount({
      domain_slug,
      grouping,
      tab: activeTab,
      page: 1,
      perPage: 50
    });
  }, [
    domain_slug,
    grouping,
    activeTab,
    searchQuery,
    skipQueries,
    getBacklinksCount
  ]);

  const tabCounts =
    location.search.includes('FBy') && isSuccess && mutationData?.data?.counts
      ? mutationData.data.counts
      : gridData?.data?.counts;

  const visibleItems = navItems.slice(0, visibleTabCount);
  const overflowItems = navItems.slice(visibleTabCount);
  const isOverflowActive = overflowItems.some(([key]) => key === activeTab);

  useEffect(() => {
    setActiveTab(requestedTab);
  }, [requestedTab]);

  const handleGroupingChange = (grouping: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('grouping', grouping);
    setSearchParams(params, { replace: true });
  };

  const handleTabChange = (tab: string) => {
    const params = new URLSearchParams(searchParams);
    if (tab === 'all') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    setActiveTab(tab);
    setSearchParams(params, { replace: true });
    setMoreOpen(false);
  };

  useLayoutEffect(() => {
    const header = headerRef.current;
    const measureRoot = measureRef.current;
    if (!header || !measureRoot) return undefined;

    const updateVisibleTabs = () => {
      const availableWidth = header.clientWidth;
      const measuredWidths = navItems.map(([key]) => {
        const width = measureRoot.querySelector<HTMLElement>(
          `[data-tab-key="${key}"]`
        )?.offsetWidth;
        if (width) tabWidthsRef.current[key] = width;
        return width || tabWidthsRef.current[key] || 0;
      });

      // Wait until at least one tab is measurable.
      if (!measuredWidths.some(width => width > 0)) return;

      let usedWidth = 0;
      let count = navItems.length;

      for (let index = 0; index < measuredWidths.length; index += 1) {
        const tabWidth = measuredWidths[index] || 0;
        const remainingAfter = measuredWidths.length - index - 1;
        const reservedWidth = remainingAfter > 0 ? MORE_TABS_BTN_WIDTH : 0;

        if (usedWidth + tabWidth + reservedWidth <= availableWidth - 8) {
          usedWidth += tabWidth;
          continue;
        }

        count = index;
        break;
      }

      setVisibleTabCount(Math.max(1, count));
    };

    updateVisibleTabs();
    const observer = new ResizeObserver(updateVisibleTabs);
    observer.observe(header);
    return () => observer.disconnect();
  }, [tabCounts]);

  return (
    <>
      {gridData?.data && !isGridLoading ? (
        <FiltersComponent
          columns={columns}
          preQueries={searchParams.toString()}
          groupingMode={body.grouping}
          onGroupingModeChange={handleGroupingChange}
        />
      ) : (
        <FiltersComponentSkeleton />
      )}
      <Card>
        <Card.Header
          style={{ zIndex: 20 }}
          className="ps-0 pe-0 py-0 border-bottom position-relative"
        >
          <div
            ref={headerRef}
            className="d-flex align-items-stretch pe-2"
          >
          {/* Off-screen measure row: full tab widths without affecting layout */}
          <div
            ref={measureRef}
            aria-hidden
            className="position-absolute"
            style={{
              visibility: 'hidden',
              pointerEvents: 'none',
              height: 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              insetInlineStart: 0,
              top: 0
            }}
          >
            <Nav as="ul" className="nav-tabs flex-nowrap border-0 mb-0">
              {navItems.map(([key, value]) => (
                <NavItem
                  current={activeTab}
                  set={() => {}}
                  count={tabCounts?.[key]}
                  item={{ key, value }}
                  key={`measure-${key}`}
                  measure
                />
              ))}
            </Nav>
          </div>

          <Nav
            as="ul"
            className="nav-tabs flex-nowrap border-0 chart-tab tab-active-caret mb-0 flex-grow-1 min-w-0 overflow-hidden"
          >
            {visibleItems.map(([key, value]) => (
              <NavItem
                current={activeTab}
                set={handleTabChange}
                count={tabCounts?.[key]}
                item={{ key, value }}
                key={key}
              />
            ))}
          </Nav>

          {overflowItems.length > 0 && (
            <div className="flex-shrink-0 align-self-stretch d-flex chart-tab tab-active-caret">
              <button
                ref={moreBtnRef}
                type="button"
                id="backlink-more-tabs"
                className={classNames(
                  'nav-link shadow-none text-decoration-none border-0 rounded-0 h-100 px-3 bg-transparent',
                  'mb-0 text-nowrap d-flex gap-2 align-items-center navLinkForceColor py-3 cursor-pointer',
                  isOverflowActive && 'active'
                )}
                aria-expanded={moreOpen}
                aria-haspopup="menu"
                aria-label="نمایش تب‌های بیشتر"
                onClick={() => setMoreOpen(open => !open)}
              >
                <span className={isOverflowActive ? 'fw-bold' : 'fw-normal'}>
                  بیشتر
                </span>
                <FontAwesomeIcon icon={faChevronDown} size="xs" />
              </button>
              <Overlay
                target={moreBtnRef}
                show={moreOpen}
                placement="bottom-start"
                rootClose
                onHide={() => setMoreOpen(false)}
                container={
                  typeof document !== 'undefined' ? document.body : undefined
                }
                popperConfig={{ strategy: 'fixed' }}
              >
                {({
                  placement: _placement,
                  arrowProps: _arrowProps,
                  show: _show,
                  popper: _popper,
                  hasDoneInitialMeasure: _hasDoneInitialMeasure,
                  ...overlayProps
                }) => (
                  <div
                    {...overlayProps}
                    role="menu"
                    className="dropdown-menu show border py-1 shadow-sm"
                    style={{
                      ...overlayProps.style,
                      minWidth: 200,
                      zIndex: 1080
                    }}
                  >
                    {overflowItems.map(([key, value]) => (
                      <button
                        key={key}
                        type="button"
                        role="menuitem"
                        className={classNames(
                          'dropdown-item d-flex align-items-center justify-content-between gap-3 py-2',
                          activeTab === key && 'active'
                        )}
                        onClick={() => handleTabChange(key)}
                      >
                        <span>{value.label}</span>
                        {tabCounts?.[key] != null && (
                          <small className="text-600">
                            ({numberHumanizer(tabCounts[key])})
                          </small>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </Overlay>
            </div>
          )}
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {isGridError ? (
            <ApiLoadError
              error={gridError}
              isError={isGridError}
              onRetry={retryGrid}
              isRetrying={isGridFetching}
              className="m-3"
            />
          ) : isGridMutationError ? (
            <ApiLoadError
              error={gridMutationError}
              isError={isGridMutationError}
              onRetry={() => {
                resetGridMutation?.();
                retryGrid();
              }}
              isRetrying={isGridMutationLoading}
              className="m-3"
            />
          ) : gridData?.data && !isGridFetching ? (
            <div className="position-relative">
              <AgGridWrapper
                table_name={location.pathname.split('/').slice(1, 3).join('/')}
                otherOptions={{
                  rowHeight: 78,
                  headerHeight: 52
                }}
                settings={{
                  pagination: true,
                  fullscreen: false,
                  changeHeight: false,
                  resetGrid: false,
                  saveGrid: false,
                  sideBar: false,
                  export: {
                    enabled: true,
                    fetchFunction: exportTable,
                    additionalData: { ...body, tab: activeTab }
                  }
                }}
                stickyHorizontalScroll
                stickyHeader
                height="auto"
                fetchFunction={getBacklinksFunc}
                total={gridData?.data?.total}
                data={gridData?.data?.rows}
                columns={columns}
                additionalData={{ ...body, tab: activeTab }}
              />
            </div>
          ) : (
            <AgGridSkeleton height="1150px" />
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default Backlinks;

const NavItem = ({ item, count, current, set, className = '', measure = false }: Record<string, any>) => {
  const isActive = !measure && current === item.key;
  return (
    <Nav.Item
      data-tab-key={item.key}
      className="z-3 flex-shrink-0"
      as="li"
    >
      <Nav.Link
        onClick={() => set(item.key)}
        className={
          className +
          ' mb-0 text-nowrap d-flex gap-1 align-items-center navLinkForceColor py-3 cursor-pointer '
        }
        active={isActive}
      >
        <span className={isActive ? 'fw-bold' : 'fw-normal'}>
          {item.value.label}
        </span>
        <div
          className={isActive ? 'fw-bold' : 'fw-normal'}
          style={{ fontSize: '0.65rem' }}
        >
          {count != null && count !== '' && (
            <>
              <span>(</span>
              {numberHumanizer(count)}
              <span>)</span>
            </>
          )}
        </div>
        {!measure && item?.value?.tooltip ? (
          <HelpTooltip
            delayToRemove={0}
            html={item?.value?.tooltip}
            placement="top"
          />
        ) : item?.value?.tooltip ? (
          <span style={{ width: 14, display: 'inline-block' }} />
        ) : null}
      </Nav.Link>
    </Nav.Item>
  );
};
